/**
 * PaymentStatusLine — top row of the summary: payment id (with copy) and
 * payment status on the left, overall status and current location on
 * the right. Pure presentational.
 */
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator, { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import { PaymentDetail } from 'src/store/slices/paymentDetail/paymentDetailTypes';

interface PaymentStatusLineProps {
  id: string;
  statusLabel: string;
  statusType: PaymentDetail['statusType'];
  locationBadge: string;
  paymentStatus?: string;
}

/** Map a free-text payment status to a Cloudscape status-indicator colour. */
const paymentStatusType = (status: string): StatusIndicatorProps.Type => {
  const s = status.toUpperCase();
  if (/APPROV|SETTLED|SUCCE|COMPLET|POSTED|ACCEPT|\bPAID\b/.test(s)) return 'success';
  if (/REJECT|FAIL|DECLIN|ERROR|CANCEL|RETURN|VOID/.test(s)) return 'error';
  if (/PEND|PROCESS|PROGRESS|SENT|SUBMIT|HELD|QUEUE|INITIAT/.test(s)) return 'in-progress';
  return 'info';
};

export const PaymentStatusLine = ({
  id, statusLabel, statusType, locationBadge, paymentStatus,
}: PaymentStatusLineProps) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
    <SpaceBetween size="xs">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Box variant="h1">{id}</Box>
        <CopyToClipboard
          variant="icon"
          textToCopy={id}
          copyButtonAriaLabel="Copy payment id"
          copySuccessText="Payment id copied"
          copyErrorText="Failed to copy payment id"
        />
      </div>
      {paymentStatus && (
        <div>
          <Box variant="awsui-key-label">Payment status</Box>
          <StatusIndicator type={paymentStatusType(paymentStatus)}>{paymentStatus}</StatusIndicator>
        </div>
      )}
    </SpaceBetween>

    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
      <StatusIndicator type={statusType}>{statusLabel}</StatusIndicator>
      <Badge color="grey">{locationBadge}</Badge>
    </div>
  </div>
);
